import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Award, 
  BookOpen, 
  Calendar, 
  CheckCircle2, 
  ChevronDown, 
  Compass, 
  Flag, 
  GraduationCap,
  Heart, 
  Info, 
  Map, 
  Medal, 
  Star, 
  Target, 
  Users,
  Tent,
  Navigation,
  Shield,
  Globe2 as Globe,
  AlertTriangle,
  Monitor,
  Flower
} from 'lucide-react';
import { useTranslation } from '../Context/TranslationContext';

const GuideSection = () => {
  const { isTamil } = useTranslation();
  const [expandedSection, setExpandedSection] = useState(null);

  const overviewContent = {
    intro: {
      en: "The Guide section is designed for girls aged 10+ to 17 years, focusing on comprehensive development through leadership training, outdoor activities, and community service in the Guide Company.",
      ta: "சாரணியர் பிரிவு 10+ முதல் 17 வயது வரையிலான பெண்களுக்காக வடிவமைக்கப்பட்டுள்ளது, சாரணியர் கம்பெனியில் தலைமைத்துவ பயிற்சி, வெளிப்புற செயல்பாடுகள் மற்றும் சமூக சேவை மூலம் முழுமையான வளர்ச்சியில் கவனம் செலுத்துகிறது."
    },
    keyFeatures: [
      {
        title: { en: "Age Group", ta: "வயது பிரிவு" },
        content: { en: "10+ to 17 years", ta: "10+ முதல் 17 வயது வரை" },
        icon: Users
      },
      {
        title: { en: "Unit Name", ta: "அலகு பெயர்" },
        content: { en: "Guide Company", ta: "சாரணியர் கம்பெனி" },
        icon: Flag
      },
      {
        title: { en: "Motto", ta: "குறிக்கோள்" },
        content: { en: "Be Prepared", ta: "தயாராக இருங்கள்" },
        icon: Target
      }
    ],
    objectives: [
      {
        title: { en: "Personal Growth", ta: "தனிப்பட்ட வளர்ச்சி" },
        content: { 
          en: "Developing self-confidence, leadership skills, and character through progressive activities",
          ta: "முன்னேற்ற செயல்பாடுகள் மூலம் தன்னம்பிக்கை, தலைமைத்துவ திறன்கள் மற்றும் குணாதிசயத்தை வளர்த்தல்"
        }
      },
      {
        title: { en: "Skill Development", ta: "திறன் மேம்பாடு" },
        content: {
          en: "Acquiring diverse skills in outdoor activities, first aid, and community service",
          ta: "வெளிப்புற செயல்பாடுகள், முதலுதவி மற்றும் சமூக சேவையில் பல்வேறு திறன்களைப் பெறுதல்"
        }
      },
      {
        title: { en: "Social Responsibility", ta: "சமூக பொறுப்புணர்வு" },
        content: {
          en: "Fostering community engagement and environmental awareness through active service",
          ta: "செயல்பாட்டு சேவை மூலம் சமூக ஈடுபாடு மற்றும் சுற்றுச்சூழல் விழிப்புணர்வை வளர்த்தல்"
        }
      }
    ]
  };

  const sectionTitles = {
    overview: {
      title: { en: "Overview", ta: "கண்ணோட்டம்" },
    },
    advancement: {
      title: { en: "Advancement Scheme", ta: "முன்னேற்றத் திட்டம்" },
    },
    proficiencyBadges: {
      title: { en: "Proficiency Badges", ta: "திறன் பதக்கங்கள்" },
    }
  };

  const advancementScheme = [
    {
      title: { en: "Pravesh", ta: "பிரவேஷ்" },
      duration: { en: "3 months", ta: "3 மாதங்கள்" },
      requirements: [
        { en: "Learn Guide Promise and Law", ta: "சாரணியர் வாக்குறுதி மற்றும் சட்டம் கற்றல்" },
        { en: "Basic Guide signs and salutes", ta: "அடிப்படை சாரணியர் சைகைகள் மற்றும் வணக்கங்கள்" },
        { en: "Understanding of flags and ceremonies", ta: "கொடிகள் மற்றும் விழாக்கள் பற்றிய புரிதல்" },
        { en: "Regular attendance at Company meetings", ta: "கம்பெனி கூட்டங்களில் தொடர்ந்து கலந்துகொள்ளுதல்" }
      ],
      icon: Flag
    },
    {
      title: { en: "Pratham Sopan", ta: "பிரதம சோபான்" },
      duration: { en: "6 months", ta: "6 மாதங்கள்" },
      requirements: [
        { en: "Personal health and hygiene", ta: "தனிப்பட்ட உடல்நலம் மற்றும் சுகாதாரம்" },
        { en: "Basic knots and pioneering", ta: "அடிப்படை முடிச்சுகள் மற்றும் முன்னோடி பணி" },
        { en: "First aid fundamentals", ta: "முதலுதவி அடிப்படைகள்" },
        { en: "Nature observation", ta: "இயற்கை கவனிப்பு" }
      ],
      icon: Star
    },
    {
      title: { en: "Dwitiya Sopan", ta: "த்விதீய சோபான்" },
      duration: { en: "6 months", ta: "6 மாதங்கள்" },
      requirements: [
        { en: "Advanced pioneering skills", ta: "மேம்பட்ட முன்னோடி திறன்கள்" },
        { en: "Cooking and campcraft", ta: "சமையல் மற்றும் முகாம் கலை" },
        { en: "Map reading and navigation", ta: "வரைபடம் வாசித்தல் மற்றும் வழிசெலுத்தல்" },
        { en: "Community service projects", ta: "சமூக சேவை திட்டங்கள்" }
      ],
      icon: Compass
    },
    {
      title: { en: "Tritiya Sopan", ta: "திரிதீய சோபான்" },
      duration: { en: "6 months", ta: "6 மாதங்கள்" },
      requirements: [
        { en: "Leadership in Company activities", ta: "கம்பெனி செயல்பாடுகளில் தலைமைத்துவம்" },
        { en: "Advanced first aid", ta: "மேம்பட்ட முதலுதவி" },
        { en: "Specialized proficiency badges", ta: "சிறப்பு திறன் பதக்கங்கள்" },
        { en: "Environmental conservation", ta: "சுற்றுச்சூழல் பாதுகாப்பு" }
      ],
      icon: Medal
    },
    {
      title: { en: "Rajya Puraskar", ta: "ராஜ்ய புரஸ்கார்" },
      duration: { en: "9 months", ta: "9 மாதங்கள்" },
      requirements: [
        { en: "Hold Tritiya Sopan", ta: "திரிதீய சோபான் பெற்றிருத்தல்" },
        { en: "Complete Ambulance Badge", ta: "ஆம்புலன்ஸ் பதக்கம் பெறுதல்" },
        { en: "Service projects leadership", ta: "சேவை திட்டங்களில் தலைமைத்துவம்" },
        { en: "Advanced outdoor skills", ta: "மேம்பட்ட வெளிப்புற திறன்கள்" }
      ],
      icon: Award
    },
    {
      title: { en: "Rashtrapati Guide", ta: "ராஷ்டிரபதி சாரணியர்" },
      special: true,
      duration: { en: "12 months", ta: "12 மாதங்கள்" },
      requirements: [
        { en: "Hold Rajya Puraskar", ta: "ராஜ்ய புரஸ்கார் பெற்றிருத்தல்" },
        { en: "Lead community development projects", ta: "சமூக மேம்பாட்டு திட்டங்களில் தலைமை" },
        { en: "Advanced skill proficiency", ta: "மேம்பட்ட திறன் தேர்ச்சி" },
        { en: "Exemplary leadership", ta: "முன்மாதிரி தலைமைத்துவம்" }
      ],
      icon: Award
    }
  ];

  const proficiencyBadges = [
    {
      category: { en: "Outdoor Skills", ta: "வெளிப்புற திறன்கள்" },
      badges: [
        { name: { en: "Camper", ta: "முகாமாளர்" }, icon: Tent },
        { name: { en: "Pioneer", ta: "முன்னோடி" }, icon: Map },
        { name: { en: "Naturalist", ta: "இயற்கையாளர்" }, icon: Flower }
      ]
    },
    {
      category: { en: "Healthcare", ta: "சுகாதாரம்" },
      badges: [
        { name: { en: "Ambulance", ta: "ஆம்புலன்ஸ்" }, icon: Heart },
        { name: { en: "Health Education", ta: "சுகாதார கல்வி" }, icon: Users },
        { name: { en: "First Aid", ta: "முதலுதவி" }, icon: Shield }
      ]
    },
    {
      category: { en: "Community Service", ta: "சமூக சேவை" },
      badges: [
        { name: { en: "World Conservation", ta: "உலக பாதுகாப்பு" }, icon: Globe },
        { name: { en: "Community Worker", ta: "சமூக பணியாளர்" }, icon: Users },
        { name: { en: "Hostess", ta: "விருந்தோம்பல்" }, icon: Heart }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 mt-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br bg-[#8A7968] py-20">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-white"
          >
            <h1 className="text-5xl font-bold mb-6">
              {isTamil ? "சாரணியர் பிரிவு" : "Guide Section"}
            </h1>
            <p className="text-xl opacity-90 max-w-3xl mx-auto">
              {overviewContent.intro[isTamil ? 'ta' : 'en']}
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4">
        {/* Overview Section */}
        <section className="py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {sectionTitles.overview.title[isTamil ? 'ta' : 'en']}
            </h2>
            <div className="w-20 h-1 bg-[#8A7968] mx-auto"></div>
          </div>

          {/* Key Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {overviewContent.keyFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-[#8A7968] bg-opacity-10 rounded-lg text-[#8A7968]">
                    <feature.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold">
                    {feature.title[isTamil ? 'ta' : 'en']}
                  </h3>
                </div>
                <p className="text-gray-600">
                  {feature.content[isTamil ? 'ta' : 'en']}
                </p>
              </motion.div>
            ))}
          </div>

       {/* Objectives */}
       <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {overviewContent.objectives.map((objective, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg"
              >
                <h3 className="text-xl font-semibold mb-4">
                  {objective.title[isTamil ? 'ta' : 'en']}
                </h3>
                <p className="text-gray-600">
                  {objective.content[isTamil ? 'ta' : 'en']}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Advancement Section */}
        <section className="py-16 bg-white">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {sectionTitles.advancement.title[isTamil ? 'ta' : 'en']}
            </h2>
            <div className="w-20 h-1 bg-[#8A7968] mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {advancementScheme.map((level, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white rounded-xl shadow-lg p-6 ${level.special ? 'border-2 border-yellow-500' : ''}`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className={`p-3 rounded-lg ${level.special ? 'bg-yellow-50 text-yellow-600' : 'bg-[#8A7968] bg-opacity-10 text-[#8A7968]'}`}>
                    <level.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {level.title[isTamil ? 'ta' : 'en']}
                    </h3>
                    {level.duration && (
                      <p className="text-sm text-gray-500">
                        {level.duration[isTamil ? 'ta' : 'en']}
                      </p>
                    )}
                  </div>
                </div>
                <ul className="space-y-2">
                  {level.requirements.map((req, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">
                        {req[isTamil ? 'ta' : 'en']}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Proficiency Badges Section */}
        <section className="py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {sectionTitles.proficiencyBadges.title[isTamil ? 'ta' : 'en']}
            </h2>
            <div className="w-20 h-1 bg-[#8A7968] mx-auto"></div>
          </div>

          <div className="space-y-8">
            {proficiencyBadges.map((category, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-2xl font-semibold mb-6">
                  {category.category[isTamil ? 'ta' : 'en']}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {category.badges.map((badge, bIndex) => (
                    <div key={bIndex} className="flex items-center gap-4 bg-gray-50 rounded-lg p-4">
                      <div className="p-2 bg-[#8A7968] bg-opacity-10 rounded-lg text-[#8A7968]">
                        <badge.icon className="w-5 h-5" />
                      </div>
                      <span className="text-gray-700">
                        {badge.name[isTamil ? 'ta' : 'en']}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default GuideSection;